import { Button, SearchInput, Tooltip } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { SyncIcon } from '@mm/icons';
import { useViewActions } from '@mm/state/actions/viewActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  combinedFeatureFlagsSelector,
  nextBillingTypeForFeatureSelector,
} from '@mm/state/selectors/featureFlagsSelectors';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import React from 'react';
import { useSelector } from 'react-redux';
import {
  PlanCircle,
  PlanText,
} from '../../../../../common/components/ProBadge';
import styles from './LeadsContactsToolbarButtons.module.css';
import { useAudienceViewHandlers } from './use-audience-view-handlers';
import { useImportContactsPermission } from '../../../../../common/hooks/use-import-contacts-permission';
import classNames from 'classnames';
import { SearchOutlined } from '@ant-design/icons';

const useExportContactsAction = () => {
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const activeFacebookPageId = useSelector(activeFacebookPageIdSelector);
  const { openContactsExportModal } = useModalActions();

  return (audienceId?: number) => {
    if (audienceId) {
      openContactsExportModal(null, activeChatbotId, audienceId);
    } else {
      openContactsExportModal(activeFacebookPageId);
    }
  };
};

interface P {
  activeAudienceId: number | undefined;
  audienceName: string;
  contactsSearchEnabled: boolean;
  setContactsSearchEnabled: (value: boolean) => void;
  contactsSearchValue: string;
  setContactsSearchValue: (value: string) => void;
  onSearchContacts: () => void;
  isExportDisabled: boolean | undefined;
}

export const LeadsContactsToolbarButtons = (props: P) => {
  const {
    activeAudienceId,
    audienceName,
    contactsSearchEnabled,
    setContactsSearchEnabled,
    contactsSearchValue,
    setContactsSearchValue,
    onSearchContacts,
    isExportDisabled,
  } = props;
  const canExportContacts = useSelector(
    combinedFeatureFlagsSelector,
  ).audiencesExportContacts;
  const syncContactsBillingType = useSelector((state: StoreState) =>
    nextBillingTypeForFeatureSelector(state, 'audiencesSyncContacts'),
  );
  const uploadContactsPermission = useImportContactsPermission();

  const { openUniversalUpgradeLandmineModal, openImportContactListModal } =
    useModalActions();
  const exportContacts = useExportContactsAction();
  const { showSlideoutMenu } = useViewActions();
  const { onSync } = useAudienceViewHandlers();

  const onSyncClick = () => onSync(activeAudienceId, audienceName);

  const onExportButtonClick = () => {
    if (!canExportContacts) {
      openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['LEADS__CONTACTS__GET-CSV'],
        feature: 'audiencesExportContacts',
      });
    } else {
      exportContacts(activeAudienceId);
    }
  };

  const onImportClick = () => openImportContactListModal();

  return (
    <>
      {contactsSearchEnabled ? (
        <SearchInput
          css={{ container: styles.searchInput }}
          autoFocus
          placeholder="Search a contact"
          value={contactsSearchValue}
          onChange={event => setContactsSearchValue(event.target.value)}
          onBlur={() => {
            if (!contactsSearchValue) {
              setContactsSearchEnabled(false);
            }
            onSearchContacts();
          }}
          onSearch={onSearchContacts}
        />
      ) : (
        <Tooltip
          position="top"
          color="dark"
          useHover
          noDelayClose
          anchorElement={
            <Button
              css={{
                container: classNames(styles.button, styles.buttonWithIcon),
                leftIcon: styles.leftIcon,
              }}
              color="secondary"
              leftIcon={<SearchOutlined />}
              onClick={() => setContactsSearchEnabled(true)}
            />
          }
        >
          Search
        </Tooltip>
      )}

      <Button
        color="secondary"
        onClick={onExportButtonClick}
        disabled={isExportDisabled}
        css={{ container: styles.button }}
      >
        Export
      </Button>
      <Tooltip
        position="top"
        color="dark"
        useHover
        noDelayClose
        toolTipDisabled={uploadContactsPermission.isAllowed}
        anchorElement={
          <Button
            css={{ container: styles.button }}
            disabled={!uploadContactsPermission.isAllowed}
            color="secondary"
            onClick={onImportClick}
          >
            Import
          </Button>
        }
      >
        <div className={styles.tipBody}>
          {!uploadContactsPermission.isAllowed &&
            'Connect an Email Account or activate a phone number first'}
        </div>
      </Tooltip>

      <Tooltip
        position="top"
        color="dark"
        useHover
        noDelayClose
        anchorElement={
          <Button
            css={{
              container: classNames(styles.button, styles.buttonWithIcon),
            }}
            color="secondary"
            onClick={onSyncClick}
            disabled={!activeAudienceId}
          >
            <SyncIcon version="2" size={16} />
            {syncContactsBillingType && (
              <PlanCircle
                className={styles.circle}
                billingType={syncContactsBillingType}
                size={9}
              />
            )}
          </Button>
        }
      >
        <div className={styles.tipBody}>
          <div>Sync to Facebook</div>
          {syncContactsBillingType && (
            <div>
              Available for{' '}
              <PlanText
                className={styles.inlineText}
                billingType={syncContactsBillingType}
              />{' '}
              plan
            </div>
          )}
        </div>
      </Tooltip>
      <Button
        color="secondary"
        onClick={() => showSlideoutMenu('attributesAndTags')}
        css={{ container: styles.button }}
      >
        Attributes and Tags
      </Button>
    </>
  );
};
